import { onAuthStateChanged } from "firebase/auth";
import { serverTimestamp, Timestamp } from "firebase/firestore";
import React, { createContext, useContext, useEffect, useMemo, useReducer } from 'react';
import { _showStates, consoleStart } from '../../../App';
import { isAppDeveloper } from "../../../project/appConfiguration";
import { gEnums } from "../../enums/globalEnums";
import { createRefPath } from "../../firestoreData/appData/appRefPaths";
import { _appEventAuthsCollection } from "../reducers/AppUsersAuthReducer";
import { authHandlers, authPendingTypes, authReducer, authReducerInitialState } from '../reducers/AuthReducer';
import { startConfirmationTypes, startTypes } from "../reducers/StartReducer";
import { AppContext } from "./AppProvider";
import { DatabaseContext } from "./DatabaseContext";
import { StartContext } from "./StartContext";
import { useFcm } from "./useHelpers/useFcm";
import { useRenders } from "./useHelpers/useRenders";

const _useAppAuths = true

/**
@state auth_state(`authValidated`, `loggedIn`, `currentUser`, `noAuth`)
@handlers auth_handlers (`'handleSetAuth`, `handleSignOut`, `handleSignedOut`)
@contexts [StartContext]
 */
export const AuthContext = createContext();
/** Top level provider the App
@componentContexts [StartContext]
@provides auth_state, auth_handlers 
@description Returns the Auth object for the App. Will return a `PageDimmer` until the appUser && appUser.profileValidated is set  
*/
const AuthProvider = (props) => {

  // appContext
  const appContext = useContext(AppContext)
  const { app_state } = appContext ?? {}
  const { mainAuth } = app_state ?? {}

  // databaseContext
  const databaseContext = useContext(DatabaseContext)
  const { database_fns } = databaseContext ?? {}

  // startContext
  const startContext = useContext(StartContext)
  const { start_handlers, start_fns, start_state } = startContext ?? {}
  const { handleAmmend_startDimmer: hasd, handleAmmend_startConfirmation: hasc } = start_handlers ?? {}
  const { startType } = start_state ?? {}

  useRenders('AuthProvider', true)

  // AuthReducer
  const initState = authReducerInitialState({ authValidated: false, mainAuth })
  const [auth_state, auth_dispatch] = useReducer(authReducer, initState);

  const auth_handlers = useMemo(
    () => authHandlers(auth_dispatch),
    [auth_dispatch]
  );

  const {
    authPendingType,
    authValidated,
    authIndex,
    signInResponse,
  } = auth_state;

  const { emailValidationStatus } = signInResponse ?? {};

  const { sendFcm_authTopic } = useFcm()

  if (_showStates.any && _showStates.auth) {
    console.log('--- auth ---')
    console.log(Object.keys(auth_state))
    console.log(Object.keys(auth_handlers))
  }

  if (start_fns && start_fns.useTimeMeasurement) {
    start_fns.useTimeMeasurement('AuthProvider', authValidated)
  }

  const eventSettingsKey = useMemo(() => {
    const hash = window.location.hash; // e.g., "#/clients/abc/events/xyz" 
    const hashParts = hash.split('/');
    const eventIndex = hashParts.indexOf('events');
    let settingsKey;
    if (eventIndex > -1) {
      settingsKey = hashParts[eventIndex + 1];
    }
    return settingsKey
  }, [window.location.hash])

  // runs when `auth_state` changes 
  useEffect(() => {

    if (mainAuth) {
      const unsubscribe = onAuthStateChanged(mainAuth, user => {

        if (!authValidated) {

          // start dimmer auth

          hasd?.(startTypes.authorizing, 'AuthProvider');

          if (user) {
            const ua = {
              displayName: user.displayName,
              email: user.email,
              phoneNumber: user.phoneNumber,
              emailVerified: user.emailVerified,
              isAnonymous: user.isAnonymous,
              loggedIn: true,
              photoURL: user.photoURL,
              providerData: user.providerData,
              senderKey: user.uid,
              uid: user.uid,
              isAppDeveloper: isAppDeveloper(user.email),
              presenceStatus: 'online'
            };

            sendFcm_authTopic(user)

            if (_useAppAuths && eventSettingsKey) {
              const r = createRefPath([_appEventAuthsCollection, eventSettingsKey])
              const dtu = {
                appEventAuths: {
                  [user.email]: {
                    createdAt: Timestamp.fromMillis(Number(user.metadata.createdAt)),
                    lastLoginAt: Timestamp.fromMillis(Number(user.metadata.lastLoginAt)),
                    lastAccessedAt: serverTimestamp()
                  }
                }
              }
              database_fns.doc_set(r, dtu, true)
            }
            // sets authValidated
            auth_handlers.handleSetAuth(ua);
            consoleStart && console.log('ua', ua);
          } else {
            auth_handlers.handleSetAuth();
          }
        } else {
          auth_handlers.handleSetAuth();
        }
      });
      return () => unsubscribe();
    }
    localStorage.removeItem('documentCount');
  }, []); // Empty dependency array is correct

  useEffect(() => {
    if (!hasd || startType == startTypes.finish) return;
    if (authValidated) {
      // hasc(startConfirmationTypes.authValidated, 'AuthProvider')
      hasd(startTypes.finish, 'authValidated')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authValidated, authIndex]);

  useEffect(() => {
    if (!hasd || startType === startTypes.finish) return;
    if (authPendingType) {
      // hasc(startConfirmationTypes.authConfirmed, 'AuthProvider')
      hasd(startTypes.finish, 'authConfirmed')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authPendingType]);

  useEffect(() => {
    if (!hasd || startType === startTypes.finish) return;
    if (emailValidationStatus === gEnums.emailValidationTypes.accepted) {
      // hasc(startConfirmationTypes.authAccepted, 'AuthProvider')
      // hasd(startTypes.finish, 'accepted')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps 
  }, [emailValidationStatus]);

  const provider = () => <AuthContext.Provider value={{ auth_handlers, auth_state }} >
    {props.children}
  </AuthContext.Provider >

  return provider()

}

export default AuthProvider

